import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useCallback } from "react";
import { Button } from "@project-mgmt/ui";
import { useVentureFileUpload, uploadVentureFileDirect, } from "../api/ventureFiles";
/**
 * Allowed file types for ventures
 */
const ALLOWED_VENTURE_FILE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".pdf"];
/**
 * File upload component for ventures
 * Supports images (JPEG, PNG, GIF, WebP) and PDFs
 *
 * Requirements:
 * - 5.1: File upload preparation
 * - 5.2: Direct upload with progress tracking
 * - 5.3: File validation
 */
export const VentureFileUpload = ({ ventureId, workspaceId, onUploadComplete, onUploadError, maxFileSize = 10 * 1024 * 1024, // 10MB default
multiple = true, disabled = false, }) => {
    const fileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const { trigger: prepareUpload } = useVentureFileUpload(ventureId, workspaceId);
    /**
     * Validate a single file
     */
    const validateFile = useCallback((file) => {
        // Check file size
        if (file.size > maxFileSize) {
            return {
                isValid: false,
                error: `File "${file.name}" exceeds maximum size of ${Math.round(maxFileSize / 1024 / 1024)}MB`,
            };
        }
        // Check file type
        if (!ALLOWED_VENTURE_FILE_TYPES.includes(file.type)) {
            return {
                isValid: false,
                error: `File "${file.name}" has unsupported type. Only images (JPEG, PNG, GIF, WebP) and PDFs are allowed.`,
            };
        }
        return { isValid: true };
    }, [maxFileSize]);
    /**
     * Upload a single file
     */
    const uploadFile = useCallback(async (file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        try {
            // Add to progress tracking
            setUploadProgress((prev) => ({
                ...prev,
                [tempId]: {
                    fileId: tempId,
                    filename: file.name,
                    progress: 0,
                    status: "uploading",
                },
            }));
            // Prepare upload
            const uploadResponse = await prepareUpload({
                filename: file.name,
                fileSize: file.size,
                mimeType: file.type,
            });
            if (!uploadResponse) {
                throw new Error("Failed to prepare upload");
            }
            // Upload file directly
            const uploadedFile = await uploadVentureFileDirect(ventureId, workspaceId, file, uploadResponse.fileId, (progress) => {
                setUploadProgress((prev) => ({
                    ...prev,
                    [tempId]: {
                        ...prev[tempId],
                        progress: Math.round(progress),
                    },
                }));
            });
            // Mark as completed
            setUploadProgress((prev) => ({
                ...prev,
                [tempId]: {
                    ...prev[tempId],
                    progress: 100,
                    status: "completed",
                },
            }));
            // Notify parent
            onUploadComplete?.(uploadedFile);
            // Remove from progress after delay
            setTimeout(() => {
                setUploadProgress((prev) => {
                    const newProgress = { ...prev };
                    delete newProgress[tempId];
                    return newProgress;
                });
            }, 2000);
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Upload failed";
            // Mark as error
            setUploadProgress((prev) => ({
                ...prev,
                [tempId]: {
                    ...prev[tempId],
                    status: "error",
                    error: errorMessage,
                },
            }));
            // Notify parent
            onUploadError?.(errorMessage);
            // Remove from progress after delay
            setTimeout(() => {
                setUploadProgress((prev) => {
                    const newProgress = { ...prev };
                    delete newProgress[tempId];
                    return newProgress;
                });
            }, 5000);
        }
    }, [ventureId, workspaceId, prepareUpload, onUploadComplete, onUploadError]);
    /**
     * Handle file selection
     */
    const handleFileSelect = useCallback(async (files) => {
        if (!files.length)
            return;
        const fileArray = Array.from(files);
        // Validate all files first
        const validationResults = fileArray.map((file) => ({
            file,
            validation: validateFile(file),
        }));
        const invalidFiles = validationResults.filter((result) => !result.validation.isValid);
        if (invalidFiles.length > 0) {
            const errors = invalidFiles.map((result) => result.validation.error).join("\n");
            onUploadError?.(errors);
            return;
        }
        // Upload all valid files
        for (const { file } of validationResults) {
            await uploadFile(file);
        }
    }, [validateFile, uploadFile, onUploadError]);
    /**
     * Handle input change
     */
    const handleInputChange = useCallback((event) => {
        const files = event.target.files;
        if (files) {
            handleFileSelect(files);
        }
        // Reset input value to allow selecting the same file again
        event.target.value = "";
    }, [handleFileSelect]);
    /**
     * Handle drag over
     */
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        setIsDragOver(true);
    }, []);
    /**
     * Handle drag leave
     */
    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        setIsDragOver(false);
    }, []);
    /**
     * Handle drop
     */
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        setIsDragOver(false);
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files);
        }
    }, [handleFileSelect]);
    /**
     * Open file dialog
     */
    const openFileDialog = useCallback((event) => {
        // Only stop propagation to prevent form submission, but allow default click behavior
        event?.stopPropagation();
        fileInputRef.current?.click();
    }, []);
    const progressEntries = Object.values(uploadProgress);
    const hasActiveUploads = progressEntries.length > 0;
    const isUploading = progressEntries.some((p) => p.status === "uploading");
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: `rounded-lg border-2 border-dashed p-6 text-center transition-colors ${isDragOver ? "border-blue-400 bg-blue-50" : "border-gray-300"} ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-gray-400"}`, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, onClick: disabled ? undefined : (e) => openFileDialog(e), role: "button", tabIndex: disabled ? -1 : 0, "aria-label": "Upload files", children: [_jsx("input", { ref: fileInputRef, type: "file", multiple: multiple, accept: ALLOWED_EXTENSIONS.join(","), onChange: handleInputChange, disabled: disabled, className: "hidden", "aria-hidden": "true" }), _jsxs("div", { className: "space-y-2", children: [_jsx("div", { className: "text-4xl", "aria-hidden": "true", children: "\uD83D\uDCCE" }), _jsx("div", { className: "text-sm text-gray-600", children: isDragOver ? ("Drop files here") : (_jsxs(_Fragment, { children: ["Drop files here or ", _jsx("span", { className: "text-blue-600 underline", children: "browse" })] })) }), _jsxs("div", { className: "text-xs text-gray-500", children: ["Supported: Images (JPEG, PNG, GIF, WebP) and PDFs", _jsx("br", {}), "Max file size: ", Math.round(maxFileSize / 1024 / 1024), "MB"] })] })] }), _jsx("div", { className: "flex justify-center", children: _jsx(Button, { onClick: (e) => openFileDialog(e), disabled: disabled || isUploading, variant: "outline", type: "button", children: isUploading ? "Uploading..." : "Select Files" }) }), hasActiveUploads && (_jsxs("div", { className: "space-y-2", children: [_jsx("h4", { className: "text-sm font-medium text-gray-700", children: "Upload Progress" }), progressEntries.map((progress) => (_jsxs("div", { className: "space-y-1", children: [_jsxs("div", { className: "flex justify-between text-sm", children: [_jsx("span", { className: "mr-2 flex-1 truncate", children: progress.filename }), _jsx("span", { className: "text-gray-500", children: progress.status === "completed"
                                            ? "✅"
                                            : progress.status === "error"
                                                ? "❌"
                                                : `${progress.progress}%` })] }), progress.status === "uploading" && (_jsx("div", { className: "h-2 w-full rounded-full bg-gray-200", children: _jsx("div", { className: "h-2 rounded-full bg-blue-600 transition-all duration-300", style: { width: `${progress.progress}%` }, role: "progressbar", "aria-valuenow": progress.progress, "aria-valuemin": 0, "aria-valuemax": 100 }) })), progress.status === "error" && progress.error && (_jsx("div", { className: "text-xs text-red-600", role: "alert", children: progress.error }))] }, progress.fileId)))] }))] }));
};
